/**
 * Task #1918 / #2206 — SBC Kamailio Native Security: tipi condivisi per il
 * modulo nativo `permissions` (trunk ACL) + geoblock via htable `kam_geoblock`
 * e relativo snapshot runtime sync.
 *
 * Zod-first: ogni interface ha schema corrispondente per validazione boundary
 * (route master CRUD + sync agent backend ↔ frontend tipizzato).
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// 1. trunk ACL (mod_permissions address table)
// ---------------------------------------------------------------------------

export const TrunkAclEntrySchema = z.object({
  id: z.number().int().min(1),
  grp: z.number().int().min(1).max(255),
  ipAddr: z.string().min(1).max(50),
  mask: z.number().int().min(0).max(128),
  port: z.number().int().min(0).max(65535),
  tags: z.string().max(64),
});
export type TrunkAclEntry = z.infer<typeof TrunkAclEntrySchema>;

export const TrunkAclEntryCreateSchema = z.object({
  grp: z.number().int().min(1).max(255).default(1),
  ipAddr: z
    .string()
    .min(1)
    .max(50)
    // IPv4 dotted-quad o IPv6 minimal (validazione superficiale; mask separato).
    .regex(/^[0-9a-fA-F.:]+$/, 'IP non valido'),
  mask: z.number().int().min(0).max(128).default(32),
  port: z.number().int().min(0).max(65535).default(0),
  tags: z.string().max(64).default(''),
});
export type TrunkAclEntryCreate = z.infer<typeof TrunkAclEntryCreateSchema>;

// ---------------------------------------------------------------------------
// 2. SBC runtime snapshot (aggregato per dashboard NOC)
// ---------------------------------------------------------------------------
// Task #2075 — `mod_pipelimit` dismesso: il vettore flood per-IP è coperto
// da `mod_pike` + jail fail2ban `kamailio-flood`.
// Task #2206 — `mod_secfilter` dismesso: geoblock migrato a htable
// `kam_geoblock` (campo `geoblock`); IP blacklist → ban fail2ban permanente.

export const SbcSyncSnapshotSchema = z.object({
  enabled: z.boolean(),
  flags: z.object({
    geoblock: z.boolean(),
    trunkAcl: z.boolean(),
  }),
  counts: z.object({
    geoblock: z.number().int().min(0),
    trunkAcl: z.number().int().min(0),
  }),
  lastTickAt: z.string().datetime().nullable(),
  lastErrors: z.array(z.object({
    at: z.string().datetime(),
    source: z.string(),
    error: z.string(),
  })),
});
export type SbcSyncSnapshot = z.infer<typeof SbcSyncSnapshotSchema>;

// ---------------------------------------------------------------------------
// 3. Edge entities — Estensioni (utenti SIP validi) + DID trusted (Task #2217)
// ---------------------------------------------------------------------------
// Sola lettura dalle tabelle mirror edge proiettate dal sync agent su Kamailio:
//   - estensioni: `public.kam_valid_users` (+ `public.kam_extensions_settings`)
//   - DID trusted: `public.kamailio_dids` (key_name = DID, key_value = tenant_id)
// Entrambe tenant-scoped → filtro opzionale `tenant_id` query param.

export const SbcEdgeExtensionSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().nullable(),
  extension: z.string(),
  transportPref: z.array(z.string()).nullable(),
  codecList: z.array(z.string()).nullable(),
  srtpMode: z.string().nullable(),
});
export type SbcEdgeExtension = z.infer<typeof SbcEdgeExtensionSchema>;

export const SbcEdgeDidSchema = z.object({
  tenantId: z.string(),
  tenantName: z.string().nullable(),
  did: z.string(),
});
export type SbcEdgeDid = z.infer<typeof SbcEdgeDidSchema>;

export const SbcEdgeEntitiesSchema = z.object({
  extensions: z.object({
    count: z.number().int().min(0),
    items: z.array(SbcEdgeExtensionSchema),
  }),
  dids: z.object({
    count: z.number().int().min(0),
    items: z.array(SbcEdgeDidSchema),
  }),
});
export type SbcEdgeEntities = z.infer<typeof SbcEdgeEntitiesSchema>;

export const SbcEdgeEntitiesQuerySchema = z.object({
  tenant_id: z.string().uuid().optional(),
});
export type SbcEdgeEntitiesQuery = z.infer<typeof SbcEdgeEntitiesQuerySchema>;
